import type { FC } from 'react';
import {
  Button, Column, DataTable, DataTableSkeleton, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, Tag, Tile,
} from '@carbon/react';
import { Receipt } from '@carbon/pictograms-react';

import type { DadosDosPagamentos } from '../../../logica/dinheiro/usePagamentos';
import { PaginaDeConta } from '../../padroes/PaginaDeConta';
import { traduzirTabela } from '../../padroes/rotulos';
import { Vazio } from '../../padroes/Vazio';
import styles from './Dinheiro.module.scss';

// O HISTÓRICO DE PAGAMENTOS (/pagamentos), no Carbon: três números no alto (o que foi pago, quantas
// cobranças, o último pagamento) e a tabela das cobranças, com o status numa Tag.

const COR_DO_STATUS = { ok: 'green', warn: 'warm-gray', danger: 'red', mute: 'gray' } as const;

const CABECALHOS = [
  { key: 'titulo', header: 'Cobrança' },
  { key: 'data', header: 'Data' },
  { key: 'forma', header: 'Forma' },
  { key: 'status', header: 'Status' },
  { key: 'valor', header: 'Valor' },
];

export const Pagamentos: FC<{ p: DadosDosPagamentos }> = ({ p }) => (
  <PaginaDeConta titulo='Pagamentos' descricao='Cobranças da assinatura Maestra PRO e dos perfis desbloqueados.' larga
    trilha={[{ rotulo: 'Seus perfis', caminho: '/artists' }, { rotulo: 'Planos', caminho: '/planos' }]}>
    <Grid narrow className={styles.grade}>
      {[
        { rotulo: 'Total pago', valor: p.resumo.total },
        { rotulo: p.resumo.rotuloDaQuantidade, valor: p.resumo.quantidade },
        { rotulo: 'Último pagamento', valor: p.resumo.ultimo },
      ].map((n, i) => (
        <Column key={n.rotulo} sm={4} md={8} lg={i === 2 ? 6 : 5}>
          <Tile className={styles.bloco}>
            <p className={styles.rotuloPequeno}>{n.rotulo}</p>
            <p className={styles.numero}>{p.carregando ? '—' : n.valor}</p>
          </Tile>
        </Column>
      ))}
    </Grid>
    <div className={styles.tabela}>
      {p.carregando ? <DataTableSkeleton columnCount={5} rowCount={3} showHeader={false} showToolbar={false} headers={CABECALHOS} aria-label='Carregando os pagamentos' />
        : p.falhou ? (
          <Vazio pictograma={Receipt} titulo='Não deu para carregar o histórico agora' texto='Confira a conexão e tente de novo.'
            acao={<Button kind='tertiary' size='md' onClick={p.tentarDeNovo}>Tentar de novo</Button>} />
        ) : p.vazio ? (
          <Vazio pictograma={Receipt} titulo='Nenhum pagamento ainda' texto='As cobranças da assinatura e dos perfis desbloqueados aparecem aqui.' />
        ) : (
          <DataTable rows={p.itens.map((i) => ({ id: i.id }))} headers={CABECALHOS} translateWithId={traduzirTabela}>
            {({ rows, headers, getTableProps, getHeaderProps, getRowProps, getTableContainerProps }) => (
              <TableContainer {...getTableContainerProps()}>
                <Table {...getTableProps()} aria-label='Histórico de pagamentos'>
                  <TableHead>
                    <TableRow>
                      {headers.map((h) => {
                        const { key, ...resto } = getHeaderProps({ header: h });
                        return <TableHeader key={key} {...resto} className={h.key === 'valor' ? styles.valor : undefined}>{h.header}</TableHeader>;
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const item = p.itens.find((i) => i.id === row.id)!;
                      const { key, ...resto } = getRowProps({ row });
                      return (
                        <TableRow key={key} {...resto}>
                          <TableCell>{item.titulo}</TableCell>
                          <TableCell>{item.data}</TableCell>
                          <TableCell>{item.forma}</TableCell>
                          <TableCell><Tag type={COR_DO_STATUS[item.tom]} size='sm'>{item.status}</Tag></TableCell>
                          <TableCell className={styles.valor}>{item.valor}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        )}
    </div>
  </PaginaDeConta>
);
